/**
 * Shore-excursion section visibility and per-stop matching.
 * The section is shown only when Viator returned at least one real experience
 * or recommendation. An empty payload hides the section. Nothing is invented.
 * Per-stop itinerary suggestions reuse the same payload and real product URLs.
 */

const VIATOR_HOSTS = new Set(['www.viator.com', 'viator.com']);

export function shouldShowShoreExcursions(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
  const ports = Array.isArray(payload.ports) ? payload.ports : [];
  const recommendations = Array.isArray(payload.recommendations) ? payload.recommendations : [];
  const portHasExperience = ports.some(
    (port) => port && Array.isArray(port.experiences) && port.experiences.length > 0
  );
  return portHasExperience || recommendations.length > 0;
}

export function formatFromPrice(amount, currency) {
  if (amount == null || amount === '') return '';
  const number = Number(amount);
  if (!Number.isFinite(number)) return '';
  const code = typeof currency === 'string' && currency ? currency : 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: number % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(number);
  } catch (err) {
    return `${code} ${number.toFixed(2)}`;
  }
}

function normalizePortName(name) {
  if (typeof name !== 'string') return '';
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function portPrimaryKey(name) {
  const normalized = normalizePortName(name);
  if (!normalized) return '';
  return normalized.split(',')[0].trim();
}

/** Stable identity for a port name: the part before the first comma, lowercased. */
export function portIdentityKey(name) {
  return portPrimaryKey(name);
}

export function portsMatch(a, b) {
  const left = normalizePortName(a);
  const right = normalizePortName(b);
  if (!left || !right) return false;
  if (left === right) return true;
  const leftKey = portPrimaryKey(a);
  const rightKey = portPrimaryKey(b);
  return Boolean(leftKey && rightKey && leftKey === rightKey);
}

export function isRealViatorUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'https:' && VIATOR_HOSTS.has(parsed.hostname.toLowerCase());
  } catch (err) {
    return false;
  }
}

// MCP-4b: when tour links are on, the backend serves each card's product_url as the same-origin
// /go/t/<product code>?s=s link (it 302s to Viator). Only that exact relative shape is accepted:
// no host, no other path, no other query.
const SITE_TOUR_PATH_RE = /^\/go\/t\/[A-Za-z0-9][A-Za-z0-9_-]{0,49}\?s=s$/;

export function isSiteTourUrl(url) {
  return typeof url === 'string' && SITE_TOUR_PATH_RE.test(url.trim());
}

// A card's product_url: a real Viator https URL (the upstream shape) or the same-origin /go/t/ link.
// The port "See all" attraction URL is NOT a card link and stays on isRealViatorUrl.
export function isRealTourUrl(url) {
  return isRealViatorUrl(url) || isSiteTourUrl(url);
}

/**
 * Real Viator experiences for one itinerary stop. Empty when the payload has
 * none for that port or when product URLs are missing / non-Viator.
 */
export function experiencesForPort(payload, portName, limit = 3) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return [];
  if (typeof portName !== 'string' || !portName.trim()) return [];
  const ports = Array.isArray(payload.ports) ? payload.ports : [];
  const match = ports.find((port) => port && portsMatch(port.port_name, portName));
  if (!match || !Array.isArray(match.experiences)) return [];
  const real = match.experiences.filter(
    (experience) => experience && isRealTourUrl(experience.product_url)
  );
  const max = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : real.length;
  return real.slice(0, max);
}

/**
 * CP-159: honest per-stop note from payload.stops (backend-classified).
 * Private islands: "Shore activities on this private island are booked through the cruise line."
 * Sea days: "Day at sea" (also derived locally so it shows without a payload).
 * Embark/debark: "Before you board" / "After you disembark" timing guidance.
 */
const SEA_DAY_NAMES = new Set(['at sea', 'sea day', 'day at sea', 'a day at sea', 'fun day at sea', 'cruising', 'cruising day']);

export function isSeaDayName(name) {
  const n = normalizePortName(name);
  if (!n) return false;
  return SEA_DAY_NAMES.has(n) || n.includes('at sea') || n.startsWith('cruising');
}

export function stopNoteForDay(payload, portName, index) {
  const stops = payload && Array.isArray(payload.stops) ? payload.stops : [];
  let stop = Number.isInteger(index) ? stops[index] : null;
  if (!stop || !portsMatch(stop.port_name, portName)) {
    stop = stops.find((s) => s && portsMatch(s.port_name, portName) && s.kind !== 'embark' && s.kind !== 'debark') || null;
  }
  if (stop && typeof stop.note === 'string' && stop.note.trim()) return stop.note.trim();
  if (isSeaDayName(portName)) return 'Day at sea';
  return '';
}
